import React from 'react';
import Icon from '../../../components/AppIcon';

const ProcessingStatus = ({ status, progress, message, details, progressStats }) => {
  const getStatusIcon = () => {
    switch (status) {
      case 'processing': return 'Loader';
      case 'success': return 'CheckCircle';
      case 'error': return 'XCircle';
      default: return 'Info';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'processing': return 'text-accent';
      case 'success': return 'text-success';
      case 'error': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getBackgroundColor = () => {
    switch (status) {
      case 'processing': return 'bg-accent/5 border-accent/20';
      case 'success': return 'bg-success/5 border-success/20';
      case 'error': return 'bg-error/5 border-error/20';
      default: return 'bg-muted border-muted';
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'processing': return 'Processing CSV...';
      case 'success': return 'Upload Complete';
      case 'error': return 'Processing Failed';
      default: return 'Status';
    }
  };

  const formatETA = (seconds) => {
    if (!seconds || seconds <= 0) return null;
    if (seconds < 60) return `${Math.ceil(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.ceil(seconds % 60);
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };

  if (!status) return null;

  const iconName = getStatusIcon();
  const statusColor = getStatusColor();
  const bgColor = getBackgroundColor();
  const title = getTitle();

  return (
    <div className={`rounded-lg border p-6 ${bgColor}`}>
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${bgColor}`}>
          {status === 'processing' ? (
            <div className="animate-spin">
              <Icon name={iconName} size={24} className={statusColor} />
            </div>
          ) : (
            <Icon name={iconName} size={24} className={statusColor} />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h3 className={`text-lg font-semibold mb-1 ${statusColor}`}>
            {title}
          </h3>
          <p className="text-sm text-muted-foreground mb-3">
            {message}
          </p>

          {status === 'processing' && (
            <div className="space-y-3">
              {/* Progress Bar */}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Progress</span>
                  <span className="font-medium">{progress}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
                  <div
                    className="bg-primary h-full rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              {/* Stats Row */}
              {progressStats && (
                <div className="grid grid-cols-3 gap-3 pt-1">
                  {/* Rows Processed */}
                  <div className="bg-background/60 rounded-md px-3 py-2 border border-border/50">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <Icon name="List" size={12} className="text-primary" />
                      <span className="text-xs text-muted-foreground">Rows</span>
                    </div>
                    <p className="text-sm font-semibold text-card-foreground">
                      {progressStats?.processedRows?.toLocaleString() ?? '—'}
                      {progressStats?.totalRows > 0 && (
                        <span className="text-xs font-normal text-muted-foreground">
                          {' '}/ {progressStats?.totalRows?.toLocaleString()}
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Processing Speed */}
                  <div className="bg-background/60 rounded-md px-3 py-2 border border-border/50">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <Icon name="Zap" size={12} className="text-accent" />
                      <span className="text-xs text-muted-foreground">Speed</span>
                    </div>
                    <p className="text-sm font-semibold text-card-foreground">
                      {progressStats?.speed != null
                        ? `${progressStats?.speed?.toLocaleString()} r/s`
                        : '—'}
                    </p>
                  </div>

                  {/* ETA */}
                  <div className="bg-background/60 rounded-md px-3 py-2 border border-border/50">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <Icon name="Clock" size={12} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">ETA</span>
                    </div>
                    <p className="text-sm font-semibold text-card-foreground">
                      {progressStats?.eta != null
                        ? (formatETA(progressStats?.eta) ?? 'Calculating...')
                        : 'Calculating...'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {status === 'success' && details && (
            <div className="mt-3 p-3 bg-success/5 rounded-md border border-success/20">
              <p className="text-sm text-success-foreground">
                <strong>Processing Summary:</strong>
              </p>
              <ul className="text-xs text-success-foreground/80 mt-2 space-y-1">
                {details?.acts && <li>• Acts: {details?.acts}</li>}
                {details?.scenes && <li>• Scenes: {details?.scenes}</li>}
                {details?.lines && <li>• Lines: {details?.lines}</li>}
                {details?.characters && <li>• Unique Characters: {details?.characters}</li>}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProcessingStatus;